import { fetchAuthSession } from 'aws-amplify/auth';
import type { ApiError } from '@guabi/shared';
import { config } from './env.js';

export class ApiRequestError extends Error {
  readonly code: ApiError['code'];
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, body: ApiError) {
    super(body.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

/**
 * One fetch wrapper for the whole app. Attaches the Cognito access token,
 * unwraps the error envelope, and never swallows a failure silently.
 */
export async function api<T>(
  path: string,
  init: RequestInit & { auth?: boolean; raw?: boolean; timeoutMs?: number } = {},
): Promise<T> {
  const { auth = true, raw = false, timeoutMs, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set('Content-Type', 'application/json');

  if (auth) {
    const session = await fetchAuthSession();
    // The id token, not the access token: a Cognito access token carries no
    // `email` claim, so the API saw an empty string for every caller and stored
    // blank emails on profiles and memberships. The authorizer accepts this
    // unchanged because an id token's `aud` is the app client id.
    const token = session.tokens?.idToken?.toString();
    if (!token)
      throw new ApiRequestError(401, { code: 'unauthorized', message: 'Session expired' });
    headers.set('Authorization', `Bearer ${token}`);
  }

  // A gateway timeout returns a body we cannot parse, which would otherwise
  // surface as a generic `internal` error and be indistinguishable from a bug.
  // Aborting first lets the caller say something useful about a slow request.
  const controller = timeoutMs === undefined ? undefined : new AbortController();
  const timer =
    controller === undefined ? undefined : setTimeout(() => controller.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(`${config.VITE_API_URL}/v1${path}`, {
      ...rest,
      headers,
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') {
      throw new ApiRequestError(504, { code: 'internal', message: 'timeout' });
    }
    throw err;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }

  if (res.status === 204) return undefined as T;

  // A CSV export is not JSON; read it as text and let the caller deal with it.
  if (raw) {
    const text = await res.text();
    if (!res.ok) {
      throw new ApiRequestError(res.status, { code: 'internal', message: 'Export failed' });
    }
    return text as T;
  }

  const body = (await res.json().catch(() => ({
    code: 'internal',
    message: 'Unexpected response from the server',
  }))) as unknown;

  if (!res.ok) throw new ApiRequestError(res.status, body as ApiError);
  return body as T;
}

export const apiGet = <T>(path: string) => api<T>(path);
/**
 * A read that must not carry a token. The public wedding site and the RSVP form
 * are opened by guests who have no account, and on a shared phone a stale
 * session would otherwise attach somebody else's credentials to the request.
 */
export const apiGetPublic = <T>(path: string) => api<T>(path, { auth: false });
export const apiPost = <T>(path: string, body: unknown, opts: { auth?: boolean } = {}) =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body), ...opts });
/** Upsert: the caller supplies the identity, so repeating the call is safe. */
export const apiPut = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });
export const apiPatch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });
